import { LevelData, isSolidType } from "./level";
import { TILE } from "./types";

export interface Body {
  x: number; // center x
  y: number; // center y
  w: number;
  h: number;
  vx: number;
  vy: number;
}

export interface CollisionResult {
  hitWall: boolean;
  hitCeiling: boolean;
  onGround: boolean;
  wallDir: number; // -1 hit left wall, 1 hit right wall, 0 none
  ceilingTx: number; // tile struck on the head (valid when hitCeiling)
  ceilingTy: number;
}

export function isSolidAt(level: LevelData, x: number, y: number): boolean {
  const tx = Math.floor(x / TILE);
  const ty = Math.floor(y / TILE);
  const t = level.tiles.get(`${tx},${ty}`);
  return isSolidType(t?.type);
}

function boxHitsSolid(level: LevelData, x: number, y: number, w: number, h: number): boolean {
  const minTx = Math.floor((x - w / 2) / TILE);
  const maxTx = Math.floor((x + w / 2) / TILE);
  const minTy = Math.floor((y - h / 2) / TILE);
  const maxTy = Math.floor((y + h / 2) / TILE);
  for (let tx = minTx; tx <= maxTx; tx++) {
    for (let ty = minTy; ty <= maxTy; ty++) {
      const t = level.tiles.get(`${tx},${ty}`);
      if (isSolidType(t?.type)) return true;
    }
  }
  return false;
}

/** Move body with axis-separated tile collision. Mutates body. Returns collision info. */
export function moveBody(level: LevelData, b: Body, dt: number): CollisionResult {
  const res: CollisionResult = {
    hitWall: false,
    hitCeiling: false,
    onGround: false,
    wallDir: 0,
    ceilingTx: -1,
    ceilingTy: -1
  };
  const maxStep = 0.3;

  // horizontal
  let hx = b.vx * dt;
  while (hx !== 0) {
    const step = Math.max(-maxStep, Math.min(maxStep, hx));
    const nx = b.x + step;
    if (boxHitsSolid(level, nx, b.y, b.w, b.h)) {
      res.hitWall = true;
      res.wallDir = b.vx > 0 ? 1 : -1;
      b.vx = 0;
      break;
    }
    b.x = nx;
    hx -= step;
  }

  // vertical
  let vy = b.vy * dt;
  while (vy !== 0) {
    const step = Math.max(-maxStep, Math.min(maxStep, vy));
    const ny = b.y + step;
    if (boxHitsSolid(level, b.x, ny, b.w, b.h)) {
      if (b.vy > 0) {
        res.hitCeiling = true;
        // identify the exact solid tile the head struck (closest to body center)
        const minTx = Math.floor((b.x - b.w / 2) / TILE);
        const maxTx = Math.floor((b.x + b.w / 2) / TILE);
        const minTy = Math.floor((ny - b.h / 2) / TILE);
        const maxTy = Math.floor((ny + b.h / 2) / TILE);
        let bestDist = Infinity;
        for (let tx = minTx; tx <= maxTx; tx++) {
          for (let ty = minTy; ty <= maxTy; ty++) {
            if (!isSolidType(level.tiles.get(`${tx},${ty}`)?.type)) continue;
            const dist = Math.abs(tx + 0.5 - b.x);
            if (dist < bestDist) {
              bestDist = dist;
              res.ceilingTx = tx;
              res.ceilingTy = ty;
            }
          }
        }
      } else {
        res.onGround = true;
      }
      b.vy = 0;
      break;
    }
    b.y = ny;
    vy -= step;
  }
  return res;
}

/** Does a ground-walker at position (center x, bottom y) have solid ground ahead? */
export function hasGroundAhead(level: LevelData, x: number, bottomY: number, dir: 1 | -1): boolean {
  const probeX = x + dir * (0.55);
  return isSolidAt(level, probeX, bottomY - 0.2);
}
